import React, { useEffect, useState, useRef, Children, memo } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2, ArrowRight, Hammer, Clock, TrendingUp, ShieldCheck, Star, X } from 'lucide-react';
// Animated section wrapper
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 40
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className={className} data-id="element-0">
      {children}
    </motion.div>;
}
// Countdown timer
function CountdownTimer() {
  const [time, setTime] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return <motion.div className="flex gap-4 justify-center items-center" animate={{
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 1,
    repeat: Infinity,
    repeatDelay: 1
  }} data-id="element-1">
      <div className="bg-[#D97757] text-white px-4 py-3 rounded-lg min-w-[70px] text-center" data-id="element-2">
        <div className="text-3xl font-bold font-serif" data-id="element-3">
          {String(time.hours).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase tracking-wider" data-id="element-4">Horas</div>
      </div>
      <div className="text-2xl font-bold" data-id="element-5">:</div>
      <div className="bg-[#D97757] text-white px-4 py-3 rounded-lg min-w-[70px] text-center" data-id="element-6">
        <div className="text-3xl font-bold font-serif" data-id="element-7">
          {String(time.minutes).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase tracking-wider" data-id="element-8">Min</div>
      </div>
      <div className="text-2xl font-bold" data-id="element-9">:</div>
      <div className="bg-[#D97757] text-white px-4 py-3 rounded-lg min-w-[70px] text-center" data-id="element-10">
        <div className="text-3xl font-bold font-serif" data-id="element-11">
          {String(time.seconds).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase tracking-wider" data-id="element-12">Seg</div>
      </div>
    </motion.div>;
}
// Purchase popup notification
function PurchasePopup() {
  const [visible, setVisible] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState({
    name: '',
    city: ''
  });
  const names = ['Carlos M.', 'Roberto G.', 'Jorge L.', 'Fernando P.', 'Diego R.', 'Martín S.', 'Pablo V.', 'Gustavo H.', 'Alejandro T.', 'Sebastián F.', 'Marcelo D.', 'Javier C.', 'Lucas B.', 'Hernán M.', 'Nicolás A.'];
  const cities = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Bahía Blanca'];
  useEffect(() => {
    const showPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      setCurrentPurchase({
        name: randomName,
        city: randomCity
      });
      setVisible(true);
      setTimeout(() => {
        setVisible(false);
      }, 5000);
    };
    const interval = setInterval(showPopup, 30000);
    showPopup(); // Show immediately on mount
    return () => clearInterval(interval);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    x: -100
  }} animate={visible ? {
    opacity: 1,
    x: 0
  } : {
    opacity: 0,
    x: -100
  }} transition={{
    type: 'spring',
    stiffness: 100
  }} className="fixed bottom-6 left-6 bg-white border-2 border-[#D97757] rounded-lg shadow-2xl p-4 max-w-sm z-50" style={{
    display: visible ? 'block' : 'none'
  }} data-id="element-13">
      <button onClick={() => setVisible(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" data-id="element-14">
        <X size={16} data-id="element-15" />
      </button>
      <div className="flex items-start gap-3" data-id="element-16">
        <div className="bg-green-500 rounded-full p-2 flex-shrink-0" data-id="element-17">
          <CheckCircle2 className="text-white" size={20} data-id="element-18" />
        </div>
        <div data-id="element-19">
          <div className="font-bold text-[#3D2E22] mb-1" data-id="element-20">
            ¡Compra confirmada!
          </div>
          <div className="text-sm text-gray-600" data-id="element-21">
            <span className="font-semibold" data-id="element-22">{currentPurchase.name}</span> de{' '}
            {currentPurchase.city} acaba de comprar este libro digital
          </div>
        </div>
      </div>
    </motion.div>;
}
export function App() {
  const scrollToCompra = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="w-full min-h-screen bg-[#FAF6F0] text-[#3D2E22]" data-id="element-23">
      <PurchasePopup data-id="element-24" />

      {/* Top urgency bar */}
      <div className="bg-[#3D2E22] text-white py-3 text-center font-semibold" data-id="element-25">
        NUEVO — 6 Tomos + 5 Bonos · Descarga inmediata
      </div>

      {/* Hero Section - RESTRUCTURED: compact, fits iPhone viewport */}
      <section className="container mx-auto px-4 py-6 md:py-16 min-h-[calc(100vh-44px)] flex flex-col justify-center" data-id="element-26">
        <div className="max-w-4xl mx-auto text-center w-full" data-id="element-27">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-28">
            <div className="text-xs md:text-sm uppercase tracking-widest text-[#D97757] font-bold mb-3 md:mb-6 leading-snug px-2" data-id="element-29">
              PARA EL CARPINTERO QUE TRABAJA TODO EL DÍA Y A FIN DE MES NO
              ENTIENDE A DÓNDE FUE LA PLATA…
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} data-id="element-30">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-2 md:mb-4 leading-tight" data-id="element-31">
              Oficio Carpintería Rentable
            </h1>
            <div className="text-lg sm:text-xl md:text-3xl font-serif mb-4 md:mb-10 text-[#D97757]" data-id="element-32">
              Sistema Maestro de Producción y Ganancia
            </div>
          </motion.div>

          {/* IMAGE - smaller on mobile so it fits viewport */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="relative cursor-pointer max-w-[220px] sm:max-w-xs md:max-w-md mx-auto mb-4 md:mb-8" onClick={scrollToCompra} data-id="element-33">
            <img src="https://cdn.magicpatterns.com/uploads/jQ6vKPFAdpXfkK5F3WeBAD/WhatsApp_Image_2026-05-19_at_6.22.50_PM-removebg-preview.png" alt="Oficio Carpintería Rentable - Sistema Completo" className="w-full" data-id="element-34" />
            <div className="hidden md:block text-center mt-4 font-semibold text-lg" data-id="element-35">
              6 Tomos + 5 Bonos
            </div>
          </motion.div>

          {/* CTA + price — visible above the fold */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} data-id="element-36">
            <motion.button onClick={scrollToCompra} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="bg-[#D97757] text-white px-6 py-4 md:px-8 md:py-5 rounded-lg font-bold text-base md:text-lg inline-flex items-center gap-3 hover:bg-[#B85A3F] transition-colors mb-2 md:mb-4" data-id="element-37">
              QUIERO EL SISTEMA AHORA <ArrowRight size={20} data-id="element-38" />
            </motion.button>

            <div className="text-xs md:text-sm font-semibold" data-id="element-39">
              Pago único de $19.990 ARS · Acceso inmediato
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero details section - the description, italic line, and bullets moved here so the hero stays compact */}
      <AnimatedSection className="py-12 md:py-16" data-id="element-40">
        <div className="container mx-auto px-4" data-id="element-41">
          <div className="max-w-3xl mx-auto text-center" data-id="element-42">
            <p className="text-xl md:text-2xl mb-6 leading-relaxed" data-id="element-43">
              El Sistema Completo que Convierte tu Taller en un Negocio que
              Realmente te Deja Dinero
            </p>
            <p className="text-lg mb-10 italic border-l-4 border-[#D97757] pl-4 max-w-2xl mx-auto text-left" data-id="element-44">
              En Argentina se carpintea diferente. Con otros materiales, otros
              precios y otra realidad. Este sistema lo sabe.
            </p>

            <div className="space-y-3 mb-8 max-w-2xl mx-auto text-left" data-id="element-45">
              {['150+ planos técnicos con medidas exactas listos para ejecutar', 'Stock, tiempos, costos y precios calculados para no regalar más trabajo', 'Sistema paso a paso adaptado 100% al mercado argentino', 'Diseños modernos que justifican precios premium', 'AutoCAD, ventas, redes y presupuestos profesionales', 'Funciona para carpintero solo, con ayudante o taller pequeño'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.08
            }} className="flex items-start gap-3" data-id="element-46">
                  <CheckCircle2 className="text-[#D97757] flex-shrink-0 mt-1" size={20} data-id="element-47" />
                  <span className="text-base" data-id="element-48">{item}</span>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Planos Preview Section */}
      <AnimatedSection className="bg-white py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-49">
        <div className="container mx-auto px-4" data-id="element-50">
          <div className="max-w-6xl mx-auto" data-id="element-51">
            <div className="text-center mb-12" data-id="element-52">
              <div className="text-sm uppercase tracking-widest text-[#D97757] font-bold mb-4" data-id="element-53">
                MUESTRA REAL DEL CONTENIDO
              </div>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mb-4" data-id="element-54">
                Mirá nuestros planos
              </h2>
              <p className="text-lg md:text-xl max-w-2xl mx-auto" data-id="element-55">
                150+ fichas técnicas profesionales con medidas exactas,
                despiece, herrajes y esquemas de armado. Esto es lo que recibís
                el primer día.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-56">
              {[{
              src: 'https://cdn.magicpatterns.com/uploads/63yodZf2Cnj9mxHAefy4qF/2.png',
              alt: 'Plano técnico - Estante con lateral oculto',
              label: 'Estante con Lateral Oculto'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/9zG7sfdvaUBm5YHUxxBeEN/8.png',
              alt: 'Plano técnico - Estante doble nivel',
              label: 'Estante Doble Nivel'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/gdpDFzG5mvsfX33BKTXvHA/bajo_mesada_2.png',
              alt: 'Plano técnico - Módulo bajo mesada',
              label: 'Módulo Bajo Mesada 1500'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/w6mdgY9s233qRSCnhFLff7/8.png',
              alt: 'Plano técnico - Mesa ratona Modelo Siena',
              label: 'Mesa Ratona Modelo Siena'
            }].map((plano, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-[#FAF6F0] rounded-lg overflow-hidden border-2 border-[#3D2E22] cursor-pointer hover:shadow-2xl transition-shadow" onClick={scrollToCompra} data-id="element-57">
                  <div className="bg-white p-3 md:p-4" data-id="element-58">
                    <img src={plano.src} alt={plano.alt} className="w-full h-auto object-contain" data-id="element-59" />
                  </div>
                  <div className="p-4 md:p-5 flex items-center justify-between gap-3" data-id="element-60">
                    <div data-id="element-61">
                      <div className="text-xs uppercase tracking-wider text-[#D97757] font-bold mb-1" data-id="element-62">
                        Ficha técnica
                      </div>
                      <div className="font-serif text-lg md:text-xl font-bold" data-id="element-63">
                        {plano.label}
                      </div>
                    </div>
                    <div className="bg-[#D97757] text-white rounded-full p-2 flex-shrink-0" data-id="element-64">
                      <ArrowRight size={18} data-id="element-65" />
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="text-center mt-12" data-id="element-66">
              <p className="text-lg md:text-xl mb-6 font-semibold" data-id="element-67">
                Y estos son solo 4 de los{' '}
                <span className="text-[#D97757]" data-id="element-68">150+ planos</span> que recibís.
              </p>
              <motion.button onClick={scrollToCompra} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="bg-[#D97757] text-white px-8 py-4 rounded-lg font-bold text-base md:text-lg inline-flex items-center gap-3 hover:bg-[#B85A3F] transition-colors" data-id="element-69">
                QUIERO ACCEDER A TODOS LOS PLANOS <ArrowRight size={20} data-id="element-70" />
              </motion.button>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Problem Section */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-71">
        <div className="container mx-auto px-4" data-id="element-72">
          <div className="max-w-4xl mx-auto" data-id="element-73">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-8 text-center" data-id="element-74">
              El problema no es que sos mal carpintero.
            </h2>

            <div className="prose prose-lg max-w-none space-y-6 text-lg leading-relaxed" data-id="element-75">
              <p data-id="element-76">
                Si tenés un taller de carpintería en Argentina, sabés
                exactamente de qué hablamos.
              </p>

              <p data-id="element-77">
                Son las 10 de la noche. Terminaste de instalar una cocina. Ocho
                módulos. Cuatro días de trabajo. Materiales, herrajes,
                transporte, instalación. El cliente quedó contento. Y cuando te
                sentás a hacer los números…
              </p>

              <p className="font-bold text-2xl text-center my-8" data-id="element-78">
                La plata no está.
              </p>

              <p data-id="element-79">
                ¿Dónde fue? Fue en la placa que mediste mal y tuviste que
                reponer. Fue en las dos horas que perdiste buscando la bisagra
                que no tenías en stock. Fue en el presupuesto que hiciste "de
                cabeza" y te olvidaste de incluir el disco de corte, la nafta y
                las dos horas de diseño. Fue en el retazo de melamina que
                tiraste porque no tenías ni idea para qué servía.
              </p>

              <div className="bg-[#FAF6F0] border-l-4 border-[#D97757] p-8 my-8" data-id="element-80">
                <p className="text-2xl font-serif font-bold text-center" data-id="element-81">
                  Ese carpintero no tiene un problema de habilidad.
                </p>
                <p className="text-2xl font-serif font-bold text-center mt-2" data-id="element-82">
                  Tiene un problema de sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Lo que pasa section */}
      <AnimatedSection className="py-16 md:py-24" data-id="element-83">
        <div className="container mx-auto px-4" data-id="element-84">
          <div className="max-w-4xl mx-auto" data-id="element-85">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-86">
              Lo que pasa en la mayoría de los talleres de carpintería en
              Argentina
            </h2>

            <div className="space-y-6" data-id="element-87">
              {['Uno calcula el precio mirando lo que cobra el vecino, no lo que le cuesta a él.', 'Otro compra materiales sin llevar registro y a fin de mes no sabe cuánto tiene en el taller ni cuánto vale.', 'El que tiene más trabajo es el que más cansado está, pero no necesariamente el que más gana.', 'Todos pierden entre 1.5 y 2.5 horas por día buscando herramientas, corrigiendo errores y haciendo viajes al corralón que se podían evitar.', 'Nadie tiene un sistema de presupuestación real: cobran "lo que parece justo" y descubren que trabajaron a pérdida cuando ya entregaron el mueble.'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-4 bg-[#FAF6F0] p-6 rounded-lg border-2 border-[#3D2E22]" data-id="element-88">
                  <div className="text-[#D97757] font-bold text-2xl flex-shrink-0" data-id="element-89">
                    •
                  </div>
                  <p className="text-lg" data-id="element-90">{item}</p>
                </motion.div>)}
            </div>

            <div className="mt-12 bg-[#3D2E22] text-white p-8 rounded-lg" data-id="element-91">
              <p className="text-xl leading-relaxed" data-id="element-92">
                Y encima siempre hay uno en el grupo de WhatsApp de carpinteros
                que tiene el taller lleno, cobra bien, sus clientes lo
                recomiendan y hasta tiene lista de espera. Cuando le preguntás
                cómo lo hace, te dice "fui aprendiendo de a poco".
              </p>
              <p className="text-2xl font-serif font-bold mt-6 text-center text-[#D97757]" data-id="element-93">
                Ese carpintero no nació sabiendo gestionar un negocio.
              </p>
              <p className="text-2xl font-serif font-bold text-center" data-id="element-94">
                Siguió un sistema.
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* El cortocircuito section */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-95">
        <div className="container mx-auto px-4" data-id="element-96">
          <div className="max-w-5xl mx-auto" data-id="element-97">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-98">
              El cortocircuito real del taller de carpintería argentino
            </h2>

            <p className="text-xl text-center mb-12 max-w-3xl mx-auto" data-id="element-99">
              El problema no es la habilidad con las manos ni las ganas de
              trabajar. El taller no rinde por tres razones concretas:
            </p>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-100">
              {[{
              title: 'Razón 1: Estás calculando mal lo que vale tu trabajo.',
              content: 'Cada hora que no cobrás es trabajo que regalás. Cada costo indirecto que no incluís en el presupuesto sale de tu bolsillo. La mayoría de los carpinteros argentinos trabaja con un margen real de 15% a 20% creyendo que trabaja al 40%. La diferencia la paga el carpintero con su tiempo.'
            }, {
              title: 'Razón 2: El desorden te cuesta plata todos los días.',
              content: 'Capital dormido en materiales sin registrar. Viajes al corralón por 4 tornillos que juraba que tenía. Placas recompradas porque la medición estuvo mal. Tiempo perdido buscando en el taller lo que debería estar en su lugar. Todo eso es dinero. Y se acumula.'
            }, {
              title: 'Razón 3: Sin diferenciación visual, siempre te comparan por precio.',
              content: 'Si tus muebles se ven igual que los del carpintero más barato del barrio, el cliente elige por precio. Siempre. El carpintero que muestra diseños modernos, que presenta un presupuesto profesional, que tiene Instagram con fotos de calidad, ese no compite por precio. Ese elige a quién le trabaja.'
            }].map((razon, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.2
            }} className="bg-[#FAF6F0] p-8 rounded-lg border-2 border-[#3D2E22]" data-id="element-101">
                  <h3 className="font-serif text-xl font-bold mb-4 text-[#D97757]" data-id="element-102">
                    {razon.title}
                  </h3>
                  <p className="leading-relaxed" data-id="element-103">{razon.content}</p>
                </motion.div>)}
            </div>

            <div className="mt-12 text-center" data-id="element-104">
              <p className="text-2xl font-serif italic mb-2" data-id="element-105">
                No es mala suerte. No es que el mercado está difícil.
              </p>
              <p className="text-2xl font-serif font-bold" data-id="element-106">
                Es falta de un sistema que funcione de verdad para Argentina.
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Mid-page CTA */}
      <AnimatedSection className="py-16 bg-[#D97757]" data-id="element-107">
        <div className="container mx-auto px-4 text-center" data-id="element-108">
          <motion.button onClick={scrollToCompra} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="bg-white text-[#D97757] px-12 py-6 rounded-lg font-bold text-xl inline-flex items-center gap-3 hover:bg-[#FAF6F0] transition-colors shadow-2xl" data-id="element-109">
            SÍ, QUIERO DEJAR DE REGALAR MI TRABAJO <ArrowRight size={24} data-id="element-110" />
          </motion.button>
        </div>
      </AnimatedSection>

      {/* Solution Section */}
      <AnimatedSection className="py-16 md:py-24" data-id="element-111">
        <div className="container mx-auto px-4" data-id="element-112">
          <div className="max-w-4xl mx-auto" data-id="element-113">
            <div className="text-center mb-12" data-id="element-114">
              <div className="text-sm uppercase tracking-widest text-[#D97757] font-bold mb-4" data-id="element-115">
                LA SOLUCIÓN
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6" data-id="element-116">
                OFICIO CARPINTERÍA RENTABLE™
              </h2>
              <p className="text-xl md:text-2xl leading-relaxed mb-6" data-id="element-117">
                El sistema completo para convertir tu taller de carpintería en
                un negocio que realmente te deja dinero.
              </p>
              <p className="text-lg mb-8" data-id="element-118">
                Sin calcular precios de memoria. Sin perder horas buscando
                materiales. Sin regalar trabajo por no saber cuánto cobrar.
              </p>
              <p className="text-lg" data-id="element-119">
                Todo el sistema ya está escrito, organizado y listo para aplicar
                desde el primer día. Creado para el mercado argentino, con
                precios reales, materiales que conseguís acá y diseños que el
                cliente de hoy realmente busca.
              </p>
            </div>

            <div className="bg-[#3D2E22] text-white p-12 rounded-lg my-12" data-id="element-120">
              <p className="text-3xl md:text-4xl font-serif italic text-center leading-relaxed" data-id="element-121">
                "Cada centímetro de placa que no usás es plata que perdiste.
                Cada minuto que no cobrás es trabajo que regalaste."
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Método Desperdicio Cero */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-122">
        <div className="container mx-auto px-4" data-id="element-123">
          <div className="max-w-4xl mx-auto" data-id="element-124">
            <div className="text-center mb-8" data-id="element-125">
              <div className="inline-block bg-[#D97757] text-white px-6 py-2 rounded-full font-bold mb-6" data-id="element-126">
                QUÉ TE LLEVÁS HOY
              </div>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-center" data-id="element-127">
              El Mecanismo Central: El Método Desperdicio Cero
            </h2>

            <div className="prose prose-lg max-w-none space-y-6 text-lg" data-id="element-128">
              <p data-id="element-129">
                El error más común del carpintero que no logra rentabilizar su
                taller es creer que el problema es conseguir más trabajo.
              </p>

              <p data-id="element-130">
                El Método Desperdicio Cero es el sistema que atraviesa los 6
                Tomos y cambia para siempre cómo gestionás tu taller: la
                rentabilidad no se consigue trabajando más horas, se consigue
                eliminando todo lo que te hace perder plata sin darte cuenta.
              </p>

              <p data-id="element-131">
                El mismo sistema de stock del Tomo 2 es el que te permite
                presupuestar correctamente en el Tomo 6. El mismo orden de
                producción del Tomo 2 es el que hace que los planos del Tomo 1
                se conviertan en muebles sin errores y sin retrabajo.
              </p>

              <p className="font-bold text-xl text-center" data-id="element-132">
                Un solo sistema. En todos los aspectos del negocio. Para
                siempre.
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 6 Tomos */}
      <AnimatedSection className="py-16 md:py-24" data-id="element-133">
        <div className="container mx-auto px-4" data-id="element-134">
          <div className="max-w-6xl mx-auto" data-id="element-135">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-center" data-id="element-136">
              El Sistema Paso a Paso
            </h2>
            <p className="text-xl text-center mb-12" data-id="element-137">
              6 Tomos diseñados para llevarte del taller que sobrevive al
              negocio que escala.
            </p>

            <div className="space-y-8" data-id="element-138">
              {[{
              emoji: '📗',
              title: 'TOMO 1: AutoCAD para Carpinteros',
              subtitle: 'Del Papel a la Placa sin Errores de Medida',
              intro: 'El primer paso para dejar de perder plata en errores que se podían evitar antes de cortar.',
              bullets: ['Por qué cada error de 2mm en 18 módulos se convierte en 36mm de desvío al final', 'Configuración exacta para carpintería: unidades, capas y plantillas base', 'Los 20 comandos que usás el 90% del tiempo — ni uno más', 'Cómo extraer la lista de corte directamente del plano', 'Cómo enviar el archivo al centro de corte sin que te corten nada mal', 'Los 8 errores más comunes en AutoCAD y cómo evitar cada uno']
            }, {
              emoji: '📘',
              title: 'TOMO 2: Stock, Tiempos y Procesos',
              subtitle: 'El Sistema que te Hace Ganar',
              intro: '¿Sabés cuánto dinero tenés "dormido" en tu taller ahora mismo? La mayoría se sorprende con el número.',
              bullets: ['El Sistema Semáforo: Verde, Amarillo y Rojo para cada material', 'La planilla de stock que elimina los viajes de emergencia al corralón', 'Tabla de tiempos estándar por operación para cotizar correctamente', 'El cronograma de 7 días para una cocina completa sin retrasos', 'El protocolo de 45 pasos que garantiza la instalación perfecta a la primera', 'Los 10 minutos al final del día que te ahorran 1 hora al siguiente']
            }, {
              emoji: '📙',
              title: 'TOMO 3: Cocinas y Closets Profesionales',
              subtitle: 'Planos, Medidas y Ejecución',
              intro: 'Las cocinas y los closets son el 70% de la facturación de un taller. Acá está todo para hacerlos bien.',
              bullets: ['12 módulos completos con despiece exacto y lista de corte', 'Las medidas ergonómicas que no podés ignorar o el mueble va a ser incómodo', 'Cocinas en L, en U y lineales: distribución y orden de instalación', 'Herrajes que multiplican el precio de venta: la diferencia entre básico y premium', 'Interior de placard moderno con zapatera, cajones y organización', 'Protocolo de instalación de 45 pasos del primer taco al último herraje']
            }, {
              emoji: '📒',
              title: 'TOMO 4: Melamina Moderna',
              subtitle: 'Living, Escritorios y Estanterías que Justifican Precios Premium',
              intro: 'El cliente de hoy no quiere el mueble marrón de siempre. Acá están los diseños que ese cliente busca.',
              bullets: ['Los 5 estilos que más se piden en Argentina en 2024 y cómo ejecutar cada uno', 'Rack TV flotante con sistema de anclaje invisible paso a paso', 'Estanterías flotantes con el sistema de varillas que soporta 25kg sin moverse', '10 combinaciones de colores que se venden solas', 'Cómo presentar el diseño al cliente antes de fabricar para cerrar la venta', 'El sistema Minifix y Confirmat: uniones invisibles sin errores']
            }, {
              emoji: '📕',
              title: 'TOMO 5: Sillones, Mesas y Estructuras',
              subtitle: 'El Negocio que la Mayoría Ignora',
              intro: 'Cuando un cliente pide un sillón, la mayoría de los carpinteros lo manda al tapicero. Ese es el error.',
              bullets: ['Estructura de sillón 1, 2 y 3 cuerpos con despiece completo', 'La pata central del sillón de 3 cuerpos: por qué es no negociable', 'Sistema de cinchas, zigzag y resortes: cuál usar para cada presupuesto', 'Mesa de madera maciza con técnica de encolado de tablones', 'Maderas para exterior y tratamientos que duran años', 'Barniz, aceite y pintura paso a paso para terminaciones profesionales']
            }, {
              emoji: '📓',
              title: 'TOMO 6: Business de Carpintería',
              subtitle: 'Redes, Ventas y Presupuesto Real',
              intro: 'El carpintero que no muestra, no vende. El que vende mal, trabaja gratis.',
              bullets: ['Por qué el 85% de tus competidores no tiene presencia en redes — y qué significa eso para vos', 'Fotos profesionales con tu celular en 3 pasos', 'El video de proceso que genera consultas sin pagar publicidad', 'La fórmula del precio correcto que incluye los costos que siempre olvidás', 'El presupuesto de 3 versiones que sube el ticket promedio sin perder clientes', 'Las 5 objeciones de precio más comunes y la respuesta exacta para cada una']
            }].map((tomo, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-white p-8 rounded-lg border-2 border-[#3D2E22] shadow-lg" data-id="element-139">
                  <div className="flex items-start gap-4 mb-4" data-id="element-140">
                    <div className="text-5xl" data-id="element-141">{tomo.emoji}</div>
                    <div className="flex-1" data-id="element-142">
                      <h3 className="font-serif text-2xl font-bold mb-2" data-id="element-143">
                        {tomo.title}
                      </h3>
                      <p className="text-lg text-[#D97757] font-semibold mb-3" data-id="element-144">
                        {tomo.subtitle}
                      </p>
                      <p className="text-base italic mb-4" data-id="element-145">{tomo.intro}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 ml-16" data-id="element-146">
                    {tomo.bullets.map((bullet, j) => <li key={j} className="flex items-start gap-3" data-id="element-147">
                        <CheckCircle2 className="text-[#D97757] flex-shrink-0 mt-1" size={18} data-id="element-148" />
                        <span data-id="element-149">{bullet}</span>
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Bonos Section */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-150">
        <div className="container mx-auto px-4" data-id="element-151">
          <div className="max-w-6xl mx-auto" data-id="element-152">
            <div className="text-center mb-12" data-id="element-153">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-id="element-154">
                🎁 5 BONOS INCLUIDOS HOY
              </h2>
              <div className="inline-block bg-[#D97757] text-white px-8 py-4 rounded-lg font-bold text-2xl animate-pulse" data-id="element-155">
                GRATIS POR EL DÍA DE HOY
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-156">
              {[{
              image: 'https://cdn.magicpatterns.com/uploads/csVHL6RYY4UqxYn5iorhGo/mockup_1-removebg-preview.png',
              title: 'BONO 1 — Calculadora de Rentabilidad Pro',
              tagline: 'El precio exacto que tenés que cobrar por cada trabajo.',
              description: '7 hojas de trabajo interconectadas en Excel/Google Sheets. Cargás los datos, la calculadora te dice el precio. Nunca más de memoria. Incluye el semáforo de rentabilidad: Verde (trabajás bien), Amarillo (revisá costos), Rojo (no ejecutar ese trabajo).'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/8VGxZhMgqJgcphyBMBau3k/mock_up_2-removebg-preview.png',
              title: 'BONO 2 — 150 Diseños Modernos con Medidas',
              tagline: 'El catálogo que le mostrás al cliente para que elija.',
              description: '150 fichas técnicas organizadas por categoría con medidas exactas, materiales, herrajes y precio orientativo. El cliente elige entre tus opciones, no entre vos y la competencia.'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/kfJzPt6CEBnRt4SYVkQFKy/mock_up_3-removebg-preview.png',
              title: 'BONO 3 — Ficha de Presupuesto Profesional',
              tagline: 'El documento que cierra ventas.',
              description: 'Modelo completo editable + versión express para WhatsApp + versión de 3 opciones para maximizar el ticket + orden de trabajo interna + remito de entrega con firma del cliente.'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/eQDPaX97bdA7UY4CqadMLB/626a0321-3691-41a6-89b9-8ca047855fc0-removebg-preview.png',
              title: 'BONO 4 — Guía de Proveedores y Negociación',
              tagline: 'Cómo bajar tus costos de materiales entre un 15% y un 30%.',
              description: 'Scripts de negociación exactos para cada tipo de proveedor. Tabla de descuentos por volumen. Estrategia de acopio inteligente en Argentina con inflación. Marcas de referencia para cada material.'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/g2BLi5vv1zc2gBvcfTFQXu/mock_up_4-removebg-preview.png',
              title: 'BONO 5 — Checklist de Instalación en Obra',
              tagline: 'Para que nunca más llegues a una obra y te falte algo.',
              description: '120+ ítems de verificación en 11 secciones. Desde la pre-salida del taller hasta la firma del remito de entrega. Con versión de bolsillo para llevar en el celular.'
            }].map((bono, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-[#FAF6F0] rounded-lg overflow-hidden border-2 border-[#3D2E22] cursor-pointer hover:shadow-xl transition-shadow" onClick={scrollToCompra} data-id="element-157">
                  <div className="w-full h-80 flex items-center justify-center p-4 bg-white" data-id="element-158">
                    <img src={bono.image} alt={bono.title} className="max-h-full max-w-full object-contain" data-id="element-159" />
                  </div>
                  <div className="p-6" data-id="element-160">
                    <div className="inline-block bg-green-500 text-white px-3 py-1 rounded text-sm font-bold mb-3" data-id="element-161">
                      VALOR: GRATIS HOY
                    </div>
                    <h3 className="font-serif text-xl font-bold mb-2" data-id="element-162">
                      {bono.title}
                    </h3>
                    <p className="text-[#D97757] font-semibold mb-3" data-id="element-163">
                      {bono.tagline}
                    </p>
                    <p className="text-sm leading-relaxed" data-id="element-164">
                      {bono.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Este sistema es para vos */}
      <AnimatedSection className="py-16 md:py-24" data-id="element-165">
        <div className="container mx-auto px-4" data-id="element-166">
          <div className="max-w-4xl mx-auto" data-id="element-167">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-168">
              ESTE SISTEMA ES PARA VOS SI…
            </h2>

            <div className="space-y-4" data-id="element-169">
              {['Terminás un trabajo y no entendés por qué no te sobra plata a pesar de haber trabajado mucho.', 'Calculás tus precios "de cabeza" o mirando lo que cobra la competencia, no lo que te cuesta a vos.', 'Perdés tiempo todos los días buscando materiales, haciendo viajes al corralón o rehaciendo trabajo por errores de medida.', 'Tus muebles son buenos pero tus fotos no lo muestran y tus clientes siempre te piden descuento.', 'Querés hacer diseños modernos que justifiquen precios premium pero no sabés exactamente cómo ejecutarlos.', 'Tenés ganas de crecer pero no sabés por dónde empezar sin contratar empleados ni invertir en más maquinaria.'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-4 bg-white p-6 rounded-lg border-2 border-green-500" data-id="element-170">
                  <CheckCircle2 className="text-green-500 flex-shrink-0 mt-1" size={24} data-id="element-171" />
                  <p className="text-lg" data-id="element-172">{item}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Testimonials */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-b-2 border-[#3D2E22]" data-id="element-173">
        <div className="container mx-auto px-4" data-id="element-174">
          <div className="max-w-6xl mx-auto" data-id="element-175">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-176">
              LO QUE DICEN LOS QUE YA TRABAJAN CON SISTEMA
            </h2>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-177">
              {[{
              quote: 'Llevaba años calculando los precios mirando lo que cobraba el de enfrente. Cuando hice el cálculo con la fórmula del sistema, descubrí que estaba trabajando con un margen real del 18%. Creía que era el 40%. Ese descubrimiento solo pagó el sistema cien veces.',
              name: 'Rodrigo M.',
              info: 'Carpintero · 12 años en el oficio · Córdoba',
              initials: 'RM'
            }, {
              quote: 'El sistema de stock cambió mi taller en un mes. Ya no hago viajes de emergencia al corralón. Sé exactamente lo que tengo, lo que falta y lo que cuesta. Recuperé $95.000 en materiales que tenía sin registrar.',
              name: 'Gustavo P.',
              info: 'Taller de melamina · 8 años · GBA',
              initials: 'GP'
            }, {
              quote: 'Siempre supe hacer los muebles. No sabía mostrarlos. Empecé a publicar en Instagram siguiendo el sistema del Tomo 6 y en el primer mes tuve 3 consultas nuevas que no conocía. Una se convirtió en una cocina completa.',
              name: 'Mariela R.',
              info: 'Carpintería familiar · Rosario',
              initials: 'MR'
            }, {
              quote: 'El presupuesto de 3 versiones es lo mejor que aprendí. Antes cotizaba un solo precio y el cliente lo aceptaba o no. Ahora presento básico, standard y premium. En 8 de cada 10 casos elige la versión media o la premium. Mi ticket promedio subió un 35%.',
              name: 'Héctor V.',
              info: 'Taller propio · 15 años · Mendoza',
              initials: 'HV'
            }, {
              quote: 'Los planos con medidas exactas me cambiaron la vida. Antes medía a ojo y cada tanto algo no cerraba en la instalación. Ahora todo sale del centro de corte y encastra a la primera. Sin retrabajo. Sin recompra de placas. Sin volver a la obra a arreglar.',
              name: 'Santiago L.',
              info: 'Carpintero solo · 6 años · Santa Fe',
              initials: 'SL'
            }].map((testimonial, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-[#FAF6F0] p-8 rounded-lg border-2 border-[#3D2E22]" data-id="element-178">
                  <div className="flex gap-1 mb-4" data-id="element-179">
                    {[...Array(5)].map((_, j) => <Star key={j} className="fill-[#D97757] text-[#D97757]" size={20} data-id="element-180" />)}
                  </div>
                  <p className="text-lg mb-6 italic leading-relaxed" data-id="element-181">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-4" data-id="element-182">
                    <div className="w-12 h-12 rounded-full bg-[#D97757] text-white flex items-center justify-center font-bold" data-id="element-183">
                      {testimonial.initials}
                    </div>
                    <div data-id="element-184">
                      <div className="font-bold" data-id="element-185">{testimonial.name}</div>
                      <div className="text-sm text-gray-600" data-id="element-186">
                        {testimonial.info}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Purchase Section */}
      <section id="compra" className="py-16 md:py-24 bg-gradient-to-b from-[#FAF6F0] to-white" data-id="element-187">
        <div className="container mx-auto px-4" data-id="element-188">
          <div className="max-w-4xl mx-auto" data-id="element-189">
            <AnimatedSection data-id="element-190">
              <div className="text-center mb-12" data-id="element-191">
                <div className="text-xl font-bold mb-6" data-id="element-192">EXPIRA EN: ⏱️</div>
                <CountdownTimer data-id="element-193" />
              </div>
            </AnimatedSection>

            <AnimatedSection data-id="element-194">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-195">
                Elegí tu acceso:
              </h2>

              <div className="bg-white border-4 border-[#D97757] rounded-lg p-8 md:p-12 shadow-2xl" data-id="element-196">
                <div className="text-center mb-8" data-id="element-197">
                  <div className="inline-block bg-[#D97757] text-white px-6 py-2 rounded-full font-bold text-sm mb-4" data-id="element-198">
                    ✅ PACK COMPLETO
                  </div>
                  <h3 className="font-serif text-3xl font-bold mb-4" data-id="element-199">
                    OFICIO CARPINTERÍA RENTABLE
                  </h3>
                  <p className="text-xl mb-6" data-id="element-200">
                    6 Tomos + 5 Bonos + 150 Planos + Calculadora de Rentabilidad
                    Pro
                  </p>
                  <p className="text-lg mb-8" data-id="element-201">
                    Todo lo que necesitás para pasar del taller que sobrevive al
                    negocio que escala.
                  </p>
                </div>

                <div className="space-y-3 mb-8" data-id="element-202">
                  {['TOMO 1: AutoCAD para Carpinteros', 'TOMO 2: Stock, Tiempos y Procesos', 'TOMO 3: Cocinas y Closets Profesionales', 'TOMO 4: Melamina Moderna', 'TOMO 5: Sillones, Mesas y Estructuras', 'TOMO 6: Business de Carpintería', 'BONO 1: Calculadora de Rentabilidad Pro', 'BONO 2: 150 Diseños Modernos con Medidas', 'BONO 3: Ficha de Presupuesto Profesional', 'BONO 4: Guía de Proveedores y Negociación', 'BONO 5: Checklist de Instalación en Obra'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-203">
                      <CheckCircle2 className="text-green-500 flex-shrink-0" size={20} data-id="element-204" />
                      <span data-id="element-205">{item}</span>
                    </div>)}
                </div>

                <div className="text-center mb-8" data-id="element-206">
                  <div className="text-gray-400 line-through text-2xl mb-2" data-id="element-207">
                    $89.990 ARS
                  </div>
                  <div className="text-5xl md:text-6xl font-bold text-[#D97757] mb-4" data-id="element-208">
                    $19.990 ARS
                  </div>
                  <div className="text-lg font-semibold mb-8" data-id="element-209">
                    Pago único · Acceso inmediato · Tuyo de por vida
                  </div>
                </div>

                <motion.a href="https://carpinteriacheck.librosdigitales.pro/cart/45266238111823:1" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="block bg-[#D97757] text-white px-12 py-6 rounded-lg font-bold text-xl text-center hover:bg-[#B85A3F] transition-colors shadow-lg" data-id="element-210">
                  QUIERO EL SISTEMA COMPLETO →
                </motion.a>

                <div className="text-center mt-6 text-sm text-gray-600" data-id="element-211">
                  <div className="flex items-center justify-center gap-2 mb-2" data-id="element-212">
                    <ShieldCheck className="text-green-500" size={20} data-id="element-213" />
                    <span className="font-semibold" data-id="element-214">
                      Pago 100% seguro — Mercado Pago
                    </span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <AnimatedSection className="bg-[#FAF6F0] py-16 md:py-24 border-t-2 border-[#3D2E22]" data-id="element-215">
        <div className="container mx-auto px-4" data-id="element-216">
          <div className="max-w-4xl mx-auto" data-id="element-217">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-218">
              PREGUNTAS FRECUENTES
            </h2>

            <div className="space-y-6" data-id="element-219">
              {[{
              q: '¿En qué formato recibo el material?',
              a: 'PDF descargable + Excel/Google Sheets para la Calculadora. Acceso inmediato después del pago. Podés leerlo desde el celular, tablet o computadora. También podés imprimirlo.'
            }, {
              q: '¿Necesito saber usar AutoCAD previamente?',
              a: 'No. El Tomo 1 arranca desde la instalación y configuración básica. Si nunca usaste AutoCAD en tu vida, al terminar el Tomo 1 vas a poder diseñar y despiece cualquier mueble.'
            }, {
              q: '¿Sirve si trabajo solo sin empleados?',
              a: 'Sí. El sistema está diseñado específicamente para carpinteros solos o con 1 ayudante. La gestión de tiempos y stock del Tomo 2 es especialmente útil para talleres pequeños donde todo depende de una sola persona.'
            }, {
              q: '¿Los precios y materiales están adaptados a Argentina?',
              a: 'Sí. Todo el contenido está basado en el mercado argentino: precios en ARS, marcas que conseguís acá, normas locales, medidas estándar del mercado local. Sin adaptar nada.'
            }, {
              q: '¿Sirve si ya tengo años en el oficio?',
              a: 'Sí. La mayoría de los carpinteros con experiencia descubren que sus mayores problemas no son técnicos sino de gestión, presupuestación y ventas. Eso es exactamente lo que resuelve este sistema.'
            }, {
              q: '¿Y si ya empecé a implementar alguna cosa?',
              a: 'Cada Tomo es independiente. Podés entrar en cualquier punto y aplicar lo que más necesitás ahora. No tenés que leer los 6 Tomos en orden si ya tenés cubierta alguna área.'
            }].map((faq, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-[#FAF6F0] p-6 rounded-lg border-2 border-[#3D2E22]" data-id="element-220">
                  <h3 className="font-bold text-lg mb-3" data-id="element-221">{faq.q}</h3>
                  <p className="leading-relaxed" data-id="element-222">{faq.a}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Guarantee Section */}
      <AnimatedSection className="py-16 md:py-24 bg-[#3D2E22] text-white" data-id="element-223">
        <div className="container mx-auto px-4" data-id="element-224">
          <div className="max-w-3xl mx-auto text-center" data-id="element-225">
            <ShieldCheck className="mx-auto mb-6 text-[#D97757]" size={64} data-id="element-226" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-id="element-227">
              🛡️ GARANTÍA TOTAL 7 DÍAS
            </h2>
            <p className="text-xl mb-6" data-id="element-228">
              Usá el sistema durante una semana completa.
            </p>
            <p className="text-lg mb-4" data-id="element-229">Si no sentís:</p>
            <ul className="text-left max-w-2xl mx-auto space-y-3 mb-8" data-id="element-230">
              <li className="flex items-start gap-3" data-id="element-231">
                <CheckCircle2 className="text-[#D97757] flex-shrink-0 mt-1" size={20} data-id="element-232" />
                <span data-id="element-233">Más claridad sobre cuánto cobrar y por qué</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-234">
                <CheckCircle2 className="text-[#D97757] flex-shrink-0 mt-1" size={20} data-id="element-235" />
                <span data-id="element-236">
                  Un sistema concreto para ordenar tu stock y tu producción
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-237">
                <CheckCircle2 className="text-[#D97757] flex-shrink-0 mt-1" size={20} data-id="element-238" />
                <span data-id="element-239">
                  Herramientas reales para diferenciarte de la competencia
                </span>
              </li>
            </ul>
            <p className="text-2xl font-bold mb-2" data-id="element-240">
              Te devolvemos el 100% del dinero.
            </p>
            <p className="text-lg" data-id="element-241">
              Sin preguntas. Sin formularios. Sin vueltas.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="bg-[#FAF6F0] py-12 border-t-2 border-[#3D2E22]" data-id="element-242">
        <div className="container mx-auto px-4 text-center" data-id="element-243">
          <div className="mb-6" data-id="element-244">
            <p className="font-bold text-lg mb-2" data-id="element-245">PRODUCTO DIGITAL</p>
            <p className="text-sm text-gray-600" data-id="element-246">
              Este producto es digital y se entrega automáticamente después de
              la compra.
            </p>
          </div>
          <div className="text-sm text-gray-600 mb-6" data-id="element-247">
            Acceso inmediato · PDF + Excel descargables · Acceso de por vida
          </div>
          <div className="text-sm text-gray-500" data-id="element-248">
            © 2026 Oficio Carpintería Rentable. Todos los derechos reservados.
          </div>
          <div className="text-xs text-gray-400 mt-4" data-id="element-249">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </div>
        </div>
      </footer>
    </div>;
}